/**
 * Enhanced Driver Onboarding via OPS - Bot Flow Replication
 * Features:
 * - Step-by-step document collection (RC, DL, Emission, Insurance, Vehicle Photo)
 * - Photo upload with AI extraction OR manual data entry
 * - Google Places autocomplete for base location
 * - Data stored in onboarding_sessions first, then moved to extracted_documents
 * - Same validation as bot onboarding
 */

import React, { Component } from 'react';
import { connect } from 'react-redux';
import { GoogleMap, Marker } from '@react-google-maps/api';
import PlacesAutocompleteInput from '../../../components/PlacesAutocompleteInput';
import {
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  Button,
  Breadcrumb,
  BreadcrumbItem,
  Alert,
  Card,
  CardBody,
  CardTitle,
  Progress,
  Badge,
  Collapse,
} from 'reactstrap';
import Widget from '../../../components/Widget';
import config from '../../../config';
import { toast } from 'react-toastify';

const API_BASE = config.opsApiBase.replace('/api/v1/ops', '');
const GEOCODING_API_KEY = config.googleMapsApiKey;

const mapContainerStyle = {
  width: '100%',
  height: '350px'
};

const mapStyles = [
  { featureType: 'all', elementType: 'geometry', stylers: [{ color: '#f5f5f5' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
  { featureType: 'road.arterial', elementType: 'labels.text.fill', stylers: [{ color: '#757575' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#dadada' }] },
  { featureType: 'road.highway', elementType: 'labels.text.fill', stylers: [{ color: '#616161' }] },
  { featureType: 'road.local', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#c9c9c9' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#9e9e9e' }] }
];

const defaultCenter = {
  lat: 12.9716, // Bangalore default
  lng: 77.5946
};

class DriverOnboardingNew extends Component {
  constructor(props) {
    super(props);

    const search = (props.location && props.location.search) || '';
    const params = new URLSearchParams(search);
    const isTransporter = params.get('type') === 'transporter';
    const phoneFromQuery = (params.get('phone') || '').trim();
    const nameFromQuery = (params.get('name') || '').trim();

    this.state = {
      // Step tracking
      currentStep: 1,
      completedSteps: [],

      // Form data
      phoneNumber: isTransporter ? '' : phoneFromQuery,
      driverName: nameFromQuery,
      language: 'kannada',
      isTransporter,
      vehicleLeadId: (params.get('vehicleLeadId') || '').trim() || null,
      // Transporter vehicle: agency phone vs optional separate driver phone
      transporterPhone: isTransporter ? phoneFromQuery : '',
      driverPhone: '',
      transporterIsDriver: false,

      // RC Data
      rcData: {
        owner_name: '',
        vehicle_make: '',
        vehicle_model: '',
        vehicle_class: '',
        registration_number: '',
        engine_number: '',
        chassis_number: '',
        fuel_type: '',
        phone_number: '',
      },
      rcUrl: null,
      rcMode: 'manual', // 'photo' or 'manual'

      // DL Data
      dlData: {
        name: '',
        dl_number: '',
        date_of_birth: '',
        address: '',
        valid_till: '',
        vehicle_class: '',
        phone_number: '',
      },
      dlUrl: null,
      dlMode: 'manual',

      // Insurance Data
      insuranceData: {
        policy_number: '',
        vehicle_number: '',
        policy_start: '',
        valid_till: '',
      },
      insuranceUrl: null,
      insuranceMode: 'manual',
      skipInsurance: false,

      // Emission Data
      emissionData: {
        registration_number: '',
        certificate_number: '',
        test_date: '',
        valid_till: '',
        test_result: '',
      },
      emissionUrl: null,
      emissionMode: 'manual',
      skipEmission: false,

      // Vehicle Photo
      vehiclePhotoUrl: null,

      // Additional Info
      location: null,
      locationInput: '',
      locationSuggestions: [],
      locationLoading: false,
      vehicleType: 'open',
      bodyType: 'Open',
      specialization: 'none',
      tonnage: '',
      perKmPrice: '',
      lengthFt: '',
      breadthFt: '',
      heightFt: '',
      remarks: '',
      intraStatePermit: false,
      readyForLongTrip: false,
      longInterDistrict: false,
      longInterState: false,
      local: false,
      shiftTiming: '9am-7pm',
      upiId: '',

      // Bank Details
      accountNumber: '',
      ifscCode: '',
      bankName: '',
      branchName: '',

      // Owner/Driver distinction
      isDriverOwner: true,
      ownerName: '',
      ownerPhone: '',

      // Transporter agency linkage (vehicle onboard requires existing transporter)
      transporterUserId: null,
      transporterProfile: null,
      transporterLookupStatus: null, // null | 'loading' | 'found' | 'not_found'
      transporterLookupError: null,

      // Transporter routes for THIS vehicle (selected agency + optional new)
      routes: [],
      agencyRouteOptions: [], // from users.transporter — selectable
      routesHydratedFromAgency: false,

      // UI State
      errors: {},
      isSubmitting: false,
      isSendingTerms: false,
      termsSent: false,
      uploadingDocument: false,
      sessionCreated: false,
      successMessage: null,
      errorMessage: null,
      existingVehicles: [],
      // Phone extensions (alternate contacts for this vehicle)
      phoneExtensions: [],
      newPhoneExtInput: '',
    };
  }

  componentDidMount() {
    if (this.state.isTransporter && this.state.transporterPhone) {
      this.lookupTransporter();
    }
  }

  // ============ STEP MANAGEMENT ============

  getSteps = () => {
    const base = [
      { id: 1, name: 'Phone Number', icon: 'phone' },
      { id: 2, name: 'RC Details', icon: 'id-card' },
      { id: 3, name: 'DL Details', icon: 'drivers-license' },
      { id: 4, name: 'Insurance', icon: 'shield' },
      { id: 5, name: 'Emission', icon: 'leaf' },
      { id: 6, name: 'Vehicle Photo', icon: 'truck' },
      { id: 7, name: 'Additional Info', icon: 'map-marker' },
    ];
    if (this.state.isTransporter) {
      return [
        ...base,
        { id: 8, name: 'Routes', icon: 'road' },
        { id: 9, name: 'Review & Submit', icon: 'check-circle' },
      ];
    }
    return [
      ...base,
      { id: 8, name: 'Review & Submit', icon: 'check-circle' },
    ];
  };

  goToStep = (step) => {
    if (step <= this.state.currentStep + 1 || this.state.completedSteps.includes(step)) {
      this.setState({ currentStep: step, errors: {}, errorMessage: null });
    }
  };

  completeStep = () => {
    const { currentStep, completedSteps } = this.state;
    if (!completedSteps.includes(currentStep)) {
      this.setState({
        completedSteps: [...completedSteps, currentStep],
        currentStep: currentStep + 1,
      });
    } else {
      this.setState({ currentStep: currentStep + 1 });
    }
  };

  // ============ API CALLS ============

  normalizePhoneNumber = (phone) => {
    // Remove spaces, dashes, parentheses
    let cleaned = phone.replace(/[\s\-\(\)]/g, '');

    // Remove leading + or 0
    cleaned = cleaned.replace(/^[\+0]+/, '');

    // If starts with 91, keep as is
    if (cleaned.startsWith('91')) {
      return cleaned;
    }

    // If 10 digits, add 91
    if (cleaned.length === 10) {
      return '91' + cleaned;
    }

    // Return as is for other cases
    return cleaned;
  };

  createSession = async () => {
    const {
      phoneNumber, driverName, isDriverOwner, ownerName, ownerPhone,
      isTransporter, transporterUserId, transporterLookupStatus,
      transporterPhone, driverPhone, transporterIsDriver, transporterProfile,
    } = this.state;
    const newErrors = {};

    if (isTransporter) {
      if (transporterLookupStatus !== 'found' || !transporterUserId) {
        newErrors.transporterPhone = 'Look up an onboarded transporter phone first';
      }
      if (!driverName || driverName.trim().length < 2) {
        newErrors.driverName = 'Driver name is required';
      }
      if (!transporterIsDriver) {
        if (!driverPhone || driverPhone.trim().length < 10) {
          newErrors.driverPhone = 'Driver phone number is required';
        }
      }
    } else {
      if (!phoneNumber || phoneNumber.trim().length < 10) {
        newErrors.phoneNumber = 'Valid phone number required';
      }
      if (!driverName || driverName.trim().length < 2) {
        newErrors.driverName = 'Driver name is required';
      }
      if (!isDriverOwner) {
        if (!ownerName || ownerName.trim().length < 2) {
          newErrors.ownerName = 'Owner name is required';
        }
        if (!ownerPhone || ownerPhone.trim().length < 10) {
          newErrors.ownerPhone = 'Valid owner phone number required';
        }
      }
    }

    if (Object.keys(newErrors).length > 0) {
      this.setState({ errors: newErrors });
      return;
    }

    const agencyPhone = this.normalizePhoneNumber(
      (transporterPhone || (transporterProfile && transporterProfile.phone_number) || '').trim()
    );
    const sessionPhone = isTransporter
      ? (transporterIsDriver
          ? agencyPhone
          : this.normalizePhoneNumber(driverPhone.trim()))
      : this.normalizePhoneNumber(phoneNumber);
    const normalizedOwnerPhone = (!isDriverOwner && !isTransporter)
      ? this.normalizePhoneNumber(ownerPhone.trim())
      : '';

    this.setState({ isSubmitting: true, errorMessage: null, phoneNumber: sessionPhone });

    const params = new URLSearchParams({
      phone_number: sessionPhone,
      name: driverName.trim(),
      is_driver_owner: isTransporter
        ? (transporterIsDriver ? 'true' : 'false')
        : (isDriverOwner ? 'true' : 'false'),
      is_transporter: isTransporter ? 'true' : 'false',
    });
    if (isTransporter && transporterUserId) {
      params.append('transporter_user_id', String(transporterUserId));
    }
    if (isTransporter && !transporterIsDriver && transporterProfile) {
      // Transporter agency is the vehicle owner; driver is a separate users row
      params.append('owner_name', transporterProfile.contact_name || transporterProfile.agency_name || 'Transporter');
      params.append('owner_phone', agencyPhone);
    }
    if (!isDriverOwner && !isTransporter) {
      params.append('owner_name', ownerName.trim());
      params.append('owner_phone', normalizedOwnerPhone);
    }
    if (this.state.language) {
      params.append('language', this.state.language);
    }

    try {
      const response = await fetch(`${API_BASE}/api/v1/ops/onboarding/session/create?${params.toString()}`, {
        method: 'POST',
      });

      const data = await response.json();

      if (data.success) {
        const sd = data.session_data || {};
        this.setState({
          sessionCreated: true,
          successMessage: 'Session created! Proceed with documents.',
          phoneNumber: sessionPhone,
          rcData: sd.rc_data || this.state.rcData,
          rcUrl: sd.rc_url || this.state.rcUrl,
          dlData: sd.dl_data || this.state.dlData,
          dlUrl: sd.dl_url || this.state.dlUrl,
          insuranceData: sd.insurance_data || this.state.insuranceData,
          insuranceUrl: sd.insurance_url || this.state.insuranceUrl,
          emissionData: sd.emission_data || this.state.emissionData,
          emissionUrl: sd.emission_url || this.state.emissionUrl,
          vehiclePhotoUrl: sd.vehicle_url || this.state.vehiclePhotoUrl,
          location: sd.location || this.state.location,
          locationInput: sd.location?.address || this.state.locationInput,
          vehicleType: sd.vehicle_type || this.state.vehicleType,
          bodyType: sd.vehicle_type
            ? (String(sd.vehicle_type).toLowerCase() === 'closed'
              ? 'Closed'
              : String(sd.vehicle_type).toLowerCase() === 'container'
                ? 'Container'
                : 'Open')
            : this.state.bodyType,
          specialization: sd.specialization || this.state.specialization,
          shiftTiming: sd.shift_timing || this.state.shiftTiming,
          upiId: sd.upi_id || this.state.upiId,
          accountNumber: sd.acc_no || this.state.accountNumber,
          ifscCode: sd.ifsc_code || this.state.ifscCode,
          bankName: sd.bank || this.state.bankName,
          branchName: sd.branch_name || this.state.branchName,
          isDriverOwner: sd.is_driver_owner !== undefined ? sd.is_driver_owner : this.state.isDriverOwner,
          ownerName: sd.owner_name || this.state.ownerName,
          ownerPhone: sd.owner_phone || this.state.ownerPhone,
          isTransporter: sd.is_transporter !== undefined ? !!sd.is_transporter : this.state.isTransporter,
          transporterUserId: sd.transporter_user_id || this.state.transporterUserId,
          transporterIsDriver: this.state.transporterIsDriver,
        });
        this.completeStep();
      } else {
        this.setState({ errorMessage: data.message || data.detail || 'Failed to create session' });
      }
    } catch (error) {
      this.setState({ errorMessage: 'Network error creating session' });
    } finally {
      this.setState({ isSubmitting: false });
    }
  };

  lookupTransporter = async () => {
    const { transporterPhone } = this.state;
    if (!transporterPhone || transporterPhone.trim().length < 10) {
      this.setState({
        errors: { transporterPhone: 'Enter transporter phone first' },
        transporterLookupStatus: null,
        transporterProfile: null,
        transporterUserId: null,
      });
      return;
    }
    const normalizedPhone = this.normalizePhoneNumber(transporterPhone.trim());
    this.setState({
      transporterLookupStatus: 'loading',
      transporterLookupError: null,
      transporterPhone: normalizedPhone,
      errors: {},
    });
    try {
      const res = await fetch(
        `${API_BASE}/api/v1/ops/transporters/by-phone/${encodeURIComponent(normalizedPhone)}`
      );
      const data = await res.json();
      if (data.found && data.transporter) {
        const t = data.transporter;
        this.setState({
          transporterLookupStatus: 'found',
          transporterProfile: t,
          transporterUserId: t.user_id,
          driverName: this.state.driverName || t.contact_name || '',
          transporterLookupError: null,
          routesHydratedFromAgency: false,
        }, () => this.hydrateRoutesFromAgency(t));
      } else {
        this.setState({
          transporterLookupStatus: 'not_found',
          transporterProfile: null,
          transporterUserId: null,
          transporterLookupError:
            data.message || 'No transporter found. Onboard the transporter first.',
        });
      }
    } catch (e) {
      this.setState({
        transporterLookupStatus: 'not_found',
        transporterProfile: null,
        transporterUserId: null,
        transporterLookupError: 'Network error looking up transporter',
      });
    }
  };

  uploadDocument = async (docType, file) => {
    this.setState({ uploadingDocument: true, errorMessage: null });

    const formData = new FormData();
    formData.append('phone_number', this.state.phoneNumber);
    formData.append('document_type', docType);
    formData.append('file', file);

    try {
      const response = await fetch(`${API_BASE}/api/v1/ops/onboarding/document/upload`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      // ALWAYS Auto-fill any extracted data that came back
      if (data.extracted_data || data.s3_url) {
        if (docType === 'rc') {
          let newRcData = { ...this.state.rcData, ...data.extracted_data };
          if (data.extracted_data && data.extracted_data.vehicle_class_info) {
             newRcData.vehicle_class = data.extracted_data.vehicle_class_info.class_number;
          }
          this.setState({ rcData: newRcData, rcUrl: data.s3_url || this.state.rcUrl });
        } else if (docType === 'dl') {
          this.setState({ dlData: { ...this.state.dlData, ...data.extracted_data }, dlUrl: data.s3_url || this.state.dlUrl });
        } else if (docType === 'insurance') {
          this.setState({ insuranceData: { ...this.state.insuranceData, ...data.extracted_data }, insuranceUrl: data.s3_url || this.state.insuranceUrl });
        } else if (docType === 'emission') {
          this.setState({ emissionData: { ...this.state.emissionData, ...data.extracted_data }, emissionUrl: data.s3_url || this.state.emissionUrl });
        } else if (docType === 'vehicle' && data.s3_url) {
          this.setState({ vehiclePhotoUrl: data.s3_url });
        }
      }

      if (data.success) {
        this.setState({
          successMessage: `${docType.toUpperCase()} document uploaded successfully!`,
        });
      } else {
        if (data.vehicle_class_missing) {
           this.setState({
             rcMode: 'manual',
             errorMessage: `Vehicle name not found in our records. Please select the correct Vehicle Class manually below.`,
           });
        } else if (data.extraction_failed) {
          this.setState({
            errorMessage: `Photo too unclear. Please enter data manually or upload clearer photo.`,
          });
        } else {
          this.setState({ errorMessage: data.message || 'Upload failed' });
        }
      }
    } catch (error) {
      this.setState({ errorMessage: 'Network error during upload' });
    } finally {
      this.setState({ uploadingDocument: false });
    }
  };

  saveManualData = async (docType, data) => {
    this.setState({ isSubmitting: true, errorMessage: null });

    try {
      const response = await fetch(`${API_BASE}/api/v1/ops/onboarding/document/manual-save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone_number: this.state.phoneNumber,
          document_type: docType,
          data: data,
        }),
      });

      const result = await response.json();

      if (result.success) {
        this.setState({ successMessage: `${docType.toUpperCase()} data saved!` });
        this.completeStep();
      } else {
        this.setState({ errorMessage: result.message || 'Validation failed', errors: result.errors || {} });
      }
    } catch (error) {
      this.setState({ errorMessage: 'Network error saving data' });
    } finally {
      this.setState({ isSubmitting: false });
    }
  };

  saveAdditionalInfo = async () => {
    const {
      phoneNumber, location, vehicleType, specialization, shiftTiming, upiId, language,
      accountNumber, ifscCode, bankName, branchName
    } = this.state;

    if (!location) {
      this.setState({ errorMessage: 'Base location is required' });
      return;
    }

    this.setState({ isSubmitting: true, errorMessage: null });

    try {
      const response = await fetch(`${API_BASE}/api/v1/ops/onboarding/additional-info/save-v2`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone_number: phoneNumber,
          location: location,
          vehicle_type: vehicleType,
          specialization: specialization,
          shift_timing: shiftTiming,
          upi_id: upiId,
          shift_timing: shiftTiming,
          upi_id: upiId,
          language: language,
          // Send flat fields matching AdditionalInfoRequest model
          acc_no: accountNumber || null,
          ifsc_code: ifscCode || null,
          bank: bankName || null,
          branch_name: branchName || null,
        }),
      });

      const data = await response.json();

      if (data.success) {
        this.setState({ successMessage: 'Additional info saved!' });
        this.completeStep();
      } else {
        this.setState({ errorMessage: data.message || 'Failed to save' });
      }
    } catch (error) {
      this.setState({ errorMessage: 'Network error' });
    } finally {
      this.setState({ isSubmitting: false });
    }
  };

  addPhoneExtension = () => {
    const val = (this.state.newPhoneExtInput || '').trim();
    if (!val) return;
    if (!/^\+?\d{8,15}$/.test(val)) {
      this.setState({ errors: { ...this.state.errors, phoneExt: 'Enter a valid number (8–15 digits)' } });
      return;
    }
    if (this.state.phoneExtensions.includes(val)) {
      this.setState({ errors: { ...this.state.errors, phoneExt: 'Already added' } });
      return;
    }
    this.setState((prev) => ({
      phoneExtensions: [...prev.phoneExtensions, val],
      newPhoneExtInput: '',
      errors: { ...prev.errors, phoneExt: null },
    }));
  };

  collectManualSpecs = () => {
    const {
      rcData, bodyType, vehicleType, specialization, tonnage, perKmPrice,
      lengthFt, breadthFt, heightFt, remarks,
      phoneExtensions, newPhoneExtInput,
      intraStatePermit, readyForLongTrip, longInterDistrict, longInterState, local,
    } = this.state;

    const pendingExt = (newPhoneExtInput || '').trim();
    const allExtensions = [...(phoneExtensions || [])];
    if (pendingExt && /^\+?\d{8,15}$/.test(pendingExt) && !allExtensions.includes(pendingExt)) {
      allExtensions.push(pendingExt);
    }

    const num = (value) => {
      if (value === '' || value == null) return null;
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    };

    const body = bodyType || (String(vehicleType).toLowerCase() === 'closed' ? 'Closed' : 'Open');
    const intra = !!intraStatePermit;
    const spec = (specialization || 'none').toLowerCase();

    return {
      registration_number: (rcData.registration_number || '').trim() || null,
      vehicle_make: (rcData.vehicle_make || '').trim() || null,
      vehicle_model: (rcData.vehicle_model || '').trim() || null,
      body_type: body,
      specialization: spec === 'refrigerated' ? 'Refrigerated' : spec === 'tipper' ? 'Tipper' : 'none',
      tonnage: num(tonnage),
      per_km_price: num(perKmPrice),
      length: num(lengthFt),
      breadth: num(breadthFt),
      height: num(heightFt),
      remarks: remarks || '',
      phone_extensions: allExtensions,
      intra_state_permit: intra,
      ready_for_long_trip: intra || !!readyForLongTrip,
      long_inter_district: !!longInterDistrict,
      long_inter_state: intra || !!longInterState,
      local: !!local,
    };
  };

  persistReviewFields = async () => {
    const { phoneNumber, rcData } = this.state;
    if (!rcData.vehicle_class) {
      this.setState({ errorMessage: 'Vehicle class is required. Please select a class before continuing.' });
      return null;
    }

    const specs = this.collectManualSpecs();
    const saveResp = await fetch(`${API_BASE}/api/v1/ops/onboarding/vehicle-class/save`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        phone_number: phoneNumber,
        vehicle_class: parseInt(rcData.vehicle_class, 10),
        ...specs,
      }),
    });
    const saveData = await saveResp.json();
    if (!saveData.success) {
      this.setState({ errorMessage: saveData.message || 'Failed to save vehicle details' });
      return null;
    }
    return specs;
  };

  completeOnboarding = async () => {
    const { phoneNumber, rcData } = this.state;

    this.setState({ isSubmitting: true, errorMessage: null });

    try {
      const specs = await this.persistReviewFields();
      if (!specs) {
        this.setState({ isSubmitting: false });
        return;
      }

      const response = await fetch(`${API_BASE}/api/v1/ops/onboarding/complete?phone_number=${phoneNumber}`, {
        method: 'POST',
      });

      const data = await response.json();

      if (data.success === true) {
        if (data.vehicle_id) {
          try {
            const token = localStorage.getItem('token') || '';
            const vehiclePayload = {
              class: parseInt(rcData.vehicle_class, 10),
              body_type: specs.body_type,
              specialization: specs.specialization,
              remarks: specs.remarks,
              phone_extensions: specs.phone_extensions,
              intra_state_permit: specs.intra_state_permit,
              ready_for_long_trip: specs.ready_for_long_trip,
              long_inter_district: specs.long_inter_district,
              long_inter_state: specs.long_inter_state,
              local: specs.local,
            };
            if (specs.registration_number) vehiclePayload.registration_no = specs.registration_number;
            if (specs.vehicle_make) vehiclePayload.make = specs.vehicle_make;
            if (specs.vehicle_model) vehiclePayload.model = specs.vehicle_model;
            if (specs.tonnage != null) vehiclePayload.tonnage = specs.tonnage;
            if (specs.per_km_price != null) vehiclePayload.per_km_price = specs.per_km_price;
            if (specs.length != null) vehiclePayload.length = specs.length;
            if (specs.breadth != null) vehiclePayload.breadth = specs.breadth;
            if (specs.height != null) vehiclePayload.height = specs.height;
            await fetch(`${API_BASE}/api/v1/ops/vehicles/${data.vehicle_id}`, {
              method: 'PUT',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(vehiclePayload),
            });
          } catch (extErr) {
            console.error('Failed to save vehicle specs:', extErr);
          }
        }

        // Same onboarded check as register-vehicle: only after vehicle_id exists.
        if (data.vehicle_id && this.state.vehicleLeadId) {
          try {
            const token = localStorage.getItem('token') || '';
            await fetch(`${API_BASE}/api/v1/ops/vehicle-leads/${this.state.vehicleLeadId}`, {
              method: 'PATCH',
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                status: 'onboarded',
                onboarded_vehicle_id: String(data.vehicle_id),
              }),
            });
          } catch (leadErr) {
            console.error('Failed to mark vehicle lead onboarded:', leadErr);
          }
        }

        toast.success('Onboarding completed successfully!');
        this.setState({
          successMessage: data.message || 'Onboarding completed successfully! Driver is now active.',
          errorMessage: null,
          existingVehicles: [],
          currentStep: 999,
        });
        setTimeout(() => {
          this.props.history.push(
            this.state.vehicleLeadId ? '/app/vehicle-leads' : '/app/dispatch/vehicles',
          );
        }, 2000);
      } else {
        const msg = data.message || data.detail || 'Failed to complete onboarding';
        toast.error(msg, { autoClose: 10000 });
        this.setState({
          errorMessage: msg,
          successMessage: null,
          existingVehicles: data.existing_vehicles || [],
        });
      }
    } catch (error) {
      this.setState({ errorMessage: 'Network error completing onboarding' });
    } finally {
      this.setState({ isSubmitting: false });
    }
  };

  sendTermsToDriver = async () => {
    const { phoneNumber } = this.state;

    this.setState({ isSendingTerms: true, errorMessage: null, successMessage: null });

    try {
      const specs = await this.persistReviewFields();
      if (!specs) {
        this.setState({ isSendingTerms: false });
        return;
      }

      const response = await fetch(
        `${API_BASE}/api/v1/ops/onboarding/send-terms?phone_number=${phoneNumber}`,
        { method: 'POST' }
      );

      const data = await response.json();

      if (data.success) {
        this.setState({
          successMessage: 'Terms & Conditions sent to driver via WhatsApp. Onboarding will complete automatically once the driver taps "I Agree".',
          termsSent: true,
        });
      } else {
        this.setState({ errorMessage: data.message || 'Failed to send Terms & Conditions' });
      }
    } catch (error) {
      this.setState({ errorMessage: 'Network error sending Terms & Conditions' });
    } finally {
      this.setState({ isSendingTerms: false });
    }
  };

  // ============ FILE HANDLERS ============

  handleFileChange = (docType) => (e) => {
    const file = e.target.files[0];
    if (file) {
      this.uploadDocument(docType, file);
    }
  };

  // ============ GEOCODING ============

  // Called by PlacesAutocompleteInput when user picks a place from the live dropdown
  handlePlaceSelect = ({ address, lat, lng }) => {
    this.setState({
      location: {
        id: null,
        address,
        latitude: lat,
        longitude: lng,
      },
      locationInput: address,
      errorMessage: null,
      successMessage: null,
    });
  };

  handleRoutePlaceSelect = (index, field, { address, lat, lng, place_id }) => {
    const routes = [...this.state.routes];
    const place = {
      address,
      latitude: lat,
      longitude: lng,
      place_id: place_id || null,
    };
    routes[index] = {
      ...routes[index],
      [field]: place,
      [`${field}Input`]: address,
    };
    this.setState({ routes, errors: {}, errorMessage: null });
  };

  mapAgencyRouteToOption = (r, index) => {
    const from = r.from || {};
    const to = r.to || {};
    return {
      id: `agency-${index}`,
      source: 'agency',
      selected: true,
      vehicleClass: r.vehicle_class != null ? Number(r.vehicle_class) : null,
      from: {
        address: from.address || '',
        latitude: from.lat != null ? from.lat : from.latitude,
        longitude: from.lng != null ? from.lng : from.longitude,
        place_id: from.place_id || null,
      },
      to: {
        address: to.address || '',
        latitude: to.lat != null ? to.lat : to.latitude,
        longitude: to.lng != null ? to.lng : to.longitude,
        place_id: to.place_id || null,
      },
      fromInput: from.address || '',
      toInput: to.address || '',
      priceMin: r.price_min != null ? String(r.price_min) : '',
      priceMax:
        r.price_max != null && Number(r.price_max) !== Number(r.price_min)
          ? String(r.price_max)
          : '',
    };
  };

  hydrateRoutesFromAgency = (profile) => {
    if (!profile || this.state.routesHydratedFromAgency) return;
    const options = (profile.routes || []).map((r, i) => this.mapAgencyRouteToOption(r, i));
    this.setState({
      agencyRouteOptions: options,
      routes: [],
      routesHydratedFromAgency: true,
    });
  };

  toggleAgencyRoute = (id) => {
    this.setState((prev) => ({
      agencyRouteOptions: prev.agencyRouteOptions.map((r) =>
        r.id === id ? { ...r, selected: !r.selected } : r
      ),
      errors: {},
    }));
  };

  addRouteRow = () => {
    const vehicleClass = this.state.rcData?.vehicle_class
      ? Number(this.state.rcData.vehicle_class)
      : null;
    this.setState({
      routes: [
        ...this.state.routes,
        {
          id: `custom-${Date.now()}`,
          source: 'custom',
          selected: true,
          vehicleClass,
          from: null,
          to: null,
          fromInput: '',
          toInput: '',
          priceMin: '',
          priceMax: '',
        },
      ],
    });
  };

  removeRouteRow = (index) => {
    this.setState({
      routes: this.state.routes.filter((_, i) => i !== index),
    });
  };

  saveRoutes = async () => {
    const { phoneNumber, routes, agencyRouteOptions, rcData } = this.state;
    const newErrors = {};
    const vehicleClass = rcData?.vehicle_class ? Number(rcData.vehicle_class) : null;
    const selectedAgency = agencyRouteOptions.filter((r) => {
      if (!r.selected) return false;
      if (r.vehicleClass == null || vehicleClass == null) return true;
      return Number(r.vehicleClass) === vehicleClass;
    });
    const allRows = [...selectedAgency, ...routes];

    if (!allRows.length) {
      newErrors.routes = 'Select at least one agency route, or add a new route for this vehicle';
    }

    const payloadRoutes = [];
    allRows.forEach((row, idx) => {
      const label = row.source === 'agency' ? `Agency route` : `New route ${idx + 1}`;
      if (!row.from || row.from.latitude == null || row.from.longitude == null) {
        newErrors[`route_${idx}_from`] = `${label}: From place is required`;
      }
      const priceMin =
        row.priceMin === '' || row.priceMin == null ? null : Number(row.priceMin);
      if (priceMin != null && (Number.isNaN(priceMin) || priceMin < 0)) {
        newErrors[`route_${idx}_price`] = `${label}: Price must be >= 0`;
      }
      let priceMax = null;
      if (row.priceMax !== '' && row.priceMax != null) {
        priceMax = Number(row.priceMax);
        if (Number.isNaN(priceMax) || (priceMin != null && priceMax < priceMin)) {
          newErrors[`route_${idx}_price`] = `${label}: Max price must be >= min price`;
        }
      }
      if (!newErrors[`route_${idx}_from`] && !newErrors[`route_${idx}_price`]) {
        const vehicleClass = row.vehicleClass != null
          ? Number(row.vehicleClass)
          : (this.state.rcData?.vehicle_class ? Number(this.state.rcData.vehicle_class) : null);
        payloadRoutes.push({
          from: {
            address: row.from.address,
            latitude: row.from.latitude,
            longitude: row.from.longitude,
            place_id: row.from.place_id || null,
          },
          to:
            row.to && row.to.latitude != null
              ? {
                  address: row.to.address,
                  latitude: row.to.latitude,
                  longitude: row.to.longitude,
                  place_id: row.to.place_id || null,
                }
              : null,
          price_min: priceMin,
          price_max: priceMax != null ? priceMax : priceMin,
          ...(vehicleClass ? { vehicle_class: vehicleClass } : {}),
        });
      }
    });

    if (Object.keys(newErrors).length > 0) {
      this.setState({ errors: newErrors });
      return;
    }

    this.setState({ isSubmitting: true, errorMessage: null });
    try {
      const response = await fetch(`${API_BASE}/api/v1/ops/onboarding/routes/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone_number: phoneNumber,
          routes: payloadRoutes,
        }),
      });
      const data = await response.json();
      if (data.success) {
        this.completeStep();
      } else {
        this.setState({ errorMessage: data.message || 'Failed to save routes' });
      }
    } catch (error) {
      this.setState({ errorMessage: 'Network error saving routes' });
    } finally {
      this.setState({ isSubmitting: false });
    }
  };

  // ============ RENDER METHODS ============

  renderStepIndicator() {
    const { currentStep, completedSteps } = this.state;
    const steps = this.getSteps();
    const progress = (completedSteps.length / steps.length) * 100;

    return (
      <div className="mb-4">
        <div className="d-flex justify-content-between mb-2">
          <span>Progress</span>
          <span>{completedSteps.length}/{steps.length} Steps Complete</span>
        </div>
        <Progress value={progress} color="success" />

        <div className="d-flex justify-content-between mt-3" style={{ overflowX: 'auto' }}>
          {steps.map(step => (
            <div
              key={step.id}
              className="text-center"
              style={{
                flex: 1,
                cursor: completedSteps.includes(step.id) ? 'pointer' : 'default',
                opacity: step.id > currentStep ? 0.5 : 1
              }}
              onClick={() => this.goToStep(step.id)}
            >
              <div
                className={`badge badge-${completedSteps.includes(step.id) ? 'success' :
                  step.id === currentStep ? 'primary' : 'secondary'
                  } badge-pill p-2 mb-1`}
              >
                <i className={`fa fa-${step.icon}`} />
              </div>
              <div style={{ fontSize: '0.75rem' }}>{step.name}</div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  renderStep1_PhoneNumber() {
    const {
      phoneNumber, driverName, isDriverOwner, ownerName, ownerPhone, errors, isTransporter,
      transporterLookupStatus, transporterProfile, transporterLookupError,
    } = this.state;

    if (isTransporter) {
      const {
        transporterPhone, driverPhone, transporterIsDriver,
      } = this.state;
      return (
        <Card>
          <CardBody>
            <CardTitle tag="h5">Step 1: Link Transporter Agency</CardTitle>
            <Alert color="info">
              Enter the transporter agency phone. If they are not onboarded yet, onboard the
              transporter first from the Transporters tab. Then enter the driver for this vehicle
              (or tick if the transporter themselves is the driver).
            </Alert>
            <FormGroup>
              <Label>Transporter Phone Number *</Label>
              <div className="d-flex" style={{ gap: 8 }}>
                <Input
                  type="tel"
                  value={transporterPhone}
                  onChange={(e) => this.setState({
                    transporterPhone: e.target.value,
                    errors: {},
                    transporterLookupStatus: null,
                    transporterProfile: null,
                    transporterUserId: null,
                  })}
                  placeholder="9876543210"
                  invalid={!!errors.transporterPhone}
                />
                <Button
                  color="secondary"
                  onClick={this.lookupTransporter}
                  disabled={transporterLookupStatus === 'loading'}
                >
                  {transporterLookupStatus === 'loading' ? 'Looking up…' : 'Look up'}
                </Button>
              </div>
              {errors.transporterPhone && <div className="text-danger">{errors.transporterPhone}</div>}
            </FormGroup>

            {transporterLookupStatus === 'found' && transporterProfile && (
              <Alert color="success">
                <div><strong>{transporterProfile.agency_name}</strong></div>
                <div>Contact: {transporterProfile.contact_name}</div>
                <div>Phone: {transporterProfile.phone_number}</div>
                <div>
                  Classes:{' '}
                  {(transporterProfile.vehicle_classes || []).map((c) => `Class ${c}`).join(', ') || '—'}
                </div>
                <div style={{ marginTop: 6, fontSize: 12 }}>
                  {(transporterProfile.routes || []).slice(0, 3).map((r, i) => (
                    <div key={`tp-r-${i}`}>
                      {(r.from && r.from.address) || 'others'} → {(r.to && r.to.address) || 'others'}
                      {r.price_min != null ? ` · ₹${r.price_min}` : ''}
                      {r.price_max != null && Number(r.price_max) !== Number(r.price_min)
                        ? `–${r.price_max}`
                        : ''}
                    </div>
                  ))}
                </div>
              </Alert>
            )}

            {transporterLookupStatus === 'not_found' && (
              <Alert color="warning">
                <div>{transporterLookupError || 'Transporter not found.'}</div>
                <Button
                  color="primary"
                  size="sm"
                  className="mt-2"
                  onClick={() => {
                    window.location.href = '/app/dispatch/vehicles?tab=transporters&openOnboard=1';
                  }}
                >
                  Onboard transporter first
                </Button>
              </Alert>
            )}

            {transporterLookupStatus === 'found' && (
              <>
                <FormGroup>
                  <Label>Driver Name *</Label>
                  <Input
                    type="text"
                    value={driverName}
                    onChange={(e) => this.setState({ driverName: e.target.value, errors: {} })}
                    placeholder="Driver full name"
                    invalid={!!errors.driverName}
                  />
                  {errors.driverName && <div className="text-danger">{errors.driverName}</div>}
                </FormGroup>

                {!transporterIsDriver && (
                  <FormGroup>
                    <Label>Driver Phone Number *</Label>
                    <Input
                      type="tel"
                      value={driverPhone}
                      onChange={(e) => this.setState({ driverPhone: e.target.value, errors: {} })}
                      placeholder="9876543210"
                      invalid={!!errors.driverPhone}
                    />
                    {errors.driverPhone && <div className="text-danger">{errors.driverPhone}</div>}
                    <small className="text-muted">
                      A separate users row is created for this driver; the transporter remains the vehicle owner.
                    </small>
                  </FormGroup>
                )}

                <FormGroup check className="mb-3">
                  <Label check>
                    <Input
                      type="checkbox"
                      checked={transporterIsDriver}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        this.setState({
                          transporterIsDriver: checked,
                          driverPhone: checked ? '' : this.state.driverPhone,
                          driverName: checked
                            ? (this.state.driverName || (transporterProfile && transporterProfile.contact_name) || '')
                            : this.state.driverName,
                          errors: {},
                        });
                      }}
                    />{' '}
                    Transporter is also the driver (no separate driver phone)
                  </Label>
                </FormGroup>

                <FormGroup>
                  <Label>Preferred Language</Label>
                  <Input
                    type="select"
                    value={this.state.language}
                    onChange={(e) => this.setState({ language: e.target.value })}
                  >
                    <option value="english">English</option>
                    <option value="hindi">Hindi</option>
                    <option value="kannada">Kannada</option>
                    <option value="roman_kannada">Roman Kannada</option>
                  </Input>
                </FormGroup>
                <Button
                  color="primary"
                  onClick={this.createSession}
                  disabled={this.state.isSubmitting}
                >
                  {this.state.isSubmitting ? 'Creating…' : 'Continue to Documents'}
                </Button>
              </>
            )}
          </CardBody>
        </Card>
      );
    }

    return (
      <Card>
        <CardBody>
          <CardTitle tag="h5">
            Step 1: Enter Driver Phone Number
          </CardTitle>
          <FormGroup>
            <Label>Driver Name *</Label>
            <Input
              type="text"
              value={driverName}
              onChange={(e) => this.setState({ driverName: e.target.value, errors: {} })}
              placeholder="Enter full name"
              invalid={!!errors.driverName}
            />
            {errors.driverName && <div className="text-danger">{errors.driverName}</div>}
          </FormGroup>
          <FormGroup>
            <Label>Phone Number *</Label>
            <Input
              type="tel"
              value={phoneNumber}
              onChange={(e) => this.setState({ phoneNumber: e.target.value, errors: {} })}
              placeholder="9876543210"
              invalid={!!errors.phoneNumber}
            />
            {errors.phoneNumber && <div className="text-danger">{errors.phoneNumber}</div>}
          </FormGroup>
          <FormGroup>
            <Label>Preferred Language</Label>
            <Input
              type="select"
              value={this.state.language}
              onChange={(e) => this.setState({ language: e.target.value })}
            >
              <option value="english">English</option>
              <option value="hindi">Hindi</option>
              <option value="kannada">Kannada</option>
              <option value="roman_kannada">Roman Kannada</option>
            </Input>
          </FormGroup>

          <FormGroup>
            <Label>Is the driver also the vehicle owner?</Label>
            <div className="d-flex" style={{ gap: '1rem' }}>
              <FormGroup check>
                <Label check>
                  <Input
                    type="radio"
                    name="isDriverOwner"
                    checked={isDriverOwner}
                    onChange={() => this.setState({ isDriverOwner: true, ownerName: '', ownerPhone: '', errors: {} })}
                  />{' '}
                  Yes
                </Label>
              </FormGroup>
              <FormGroup check>
                <Label check>
                  <Input
                    type="radio"
                    name="isDriverOwner"
                    checked={!isDriverOwner}
                    onChange={() => this.setState({ isDriverOwner: false, errors: {} })}
                  />{' '}
                  No (vehicle is owned by someone else)
                </Label>
              </FormGroup>
            </div>
          </FormGroup>

          {!isDriverOwner && (
            <Alert color="info" className="mb-3">
              <strong>Owner details required.</strong> The RC is registered under a different owner.
              Enter the owner&apos;s name and phone so we can link the vehicle correctly.
              <Row className="mt-3">
                <Col md={6}>
                  <FormGroup>
                    <Label>Owner Name *</Label>
                    <Input
                      type="text"
                      value={ownerName}
                      onChange={(e) => this.setState({ ownerName: e.target.value, errors: {} })}
                      placeholder="Registered owner's full name"
                      invalid={!!errors.ownerName}
                    />
                    {errors.ownerName && <div className="text-danger">{errors.ownerName}</div>}
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label>Owner Phone *</Label>
                    <Input
                      type="tel"
                      value={ownerPhone}
                      onChange={(e) => this.setState({ ownerPhone: e.target.value, errors: {} })}
                      placeholder="Owner's phone number"
                      invalid={!!errors.ownerPhone}
                    />
                    {errors.ownerPhone && <div className="text-danger">{errors.ownerPhone}</div>}
                  </FormGroup>
                </Col>
              </Row>
            </Alert>
          )}

          <FormGroup>
            <Label>Phone Extensions / Alternate Contacts <small className="text-muted">(optional)</small></Label>
            <div style={{ marginBottom: '8px' }}>
              {this.state.phoneExtensions.length > 0 ? (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
                  {this.state.phoneExtensions.map((phone, idx) => (
                    <span
                      key={idx}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        backgroundColor: '#e8f0fe',
                        border: '1px solid #c5d4f7',
                        borderRadius: '6px',
                        padding: '3px 10px',
                        fontSize: '13px',
                        color: '#1a3c8f',
                        fontWeight: 500,
                      }}
                    >
                      📞 {phone}
                      <button
                        type="button"
                        onClick={() =>
                          this.setState(prev => ({
                            phoneExtensions: prev.phoneExtensions.filter(p => p !== phone)
                          }))
                        }
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#dc2626',
                          cursor: 'pointer',
                          fontSize: '15px',
                          fontWeight: 'bold',
                          padding: '0 2px',
                          lineHeight: 1,
                        }}
                      >
                        &times;
                      </button>
                    </span>
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: '12px', color: '#9ca3af', fontStyle: 'italic', marginBottom: '6px' }}>
                  No alternate contacts added yet.
                </div>
              )}
              <div style={{ display: 'flex', gap: '8px', maxWidth: '360px' }}>
                <input
                  type="tel"
                  value={this.state.newPhoneExtInput}
                  onChange={e => this.setState({ newPhoneExtInput: e.target.value })}
                  placeholder="e.g. 919900000000"
                  style={{
                    flex: 1,
                    padding: '6px 10px',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    fontSize: '14px',
                  }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const val = this.state.newPhoneExtInput.trim();
                      if (!val) return;
                      if (!/^\+?\d{8,15}$/.test(val)) {
                        this.setState({ errors: { ...this.state.errors, phoneExt: 'Enter a valid number (8–15 digits)' } });
                        return;
                      }
                      if (this.state.phoneExtensions.includes(val)) {
                        this.setState({ errors: { ...this.state.errors, phoneExt: 'Already added' } });
                        return;
                      }
                      this.setState(prev => ({
                        phoneExtensions: [...prev.phoneExtensions, val],
                        newPhoneExtInput: '',
                        errors: { ...prev.errors, phoneExt: null },
                      }));
                    }
                  }}
                />
                <button
                  type="button"
                  onClick={() => {
                    const val = this.state.newPhoneExtInput.trim();
                    if (!val) return;
                    if (!/^\+?\d{8,15}$/.test(val)) {
                      this.setState({ errors: { ...this.state.errors, phoneExt: 'Enter a valid number (8–15 digits)' } });
                      return;
                    }
                    if (this.state.phoneExtensions.includes(val)) {
                      this.setState({ errors: { ...this.state.errors, phoneExt: 'Already added' } });
                      return;
                    }
                    this.setState(prev => ({
                      phoneExtensions: [...prev.phoneExtensions, val],
                      newPhoneExtInput: '',
                      errors: { ...prev.errors, phoneExt: null },
                    }));
                  }}
                  style={{
                    padding: '6px 14px',
                    backgroundColor: '#375191',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '600',
                  }}
                >
                  Add
                </button>
              </div>
              {this.state.errors.phoneExt && (
                <div className="text-danger" style={{ fontSize: '12px', marginTop: '4px' }}>
                  {this.state.errors.phoneExt}
                </div>
              )}
            </div>
          </FormGroup>

          <Button color="primary" onClick={this.createSession} disabled={this.state.isSubmitting}>
            {this.state.isSubmitting ? 'Creating...' : 'Create Session & Continue'}
          </Button>
        </CardBody>
      </Card>
    );
  }

  renderDocumentStep(stepNum, title, docType, dataState, dataSetterKey, modeKey) {
    const mode = this.state[modeKey];
    const data = this.state[dataState];
    const urlKey = `${docType}Url`;
    const uploadedUrl = this.state[urlKey];

    return (
      <Card>
        <CardBody>
          <CardTitle tag="h5">{title}</CardTitle>

          <div className="mb-3">
            <Button
              color={mode === 'photo' ? 'primary' : 'outline-primary'}
              onClick={() => this.setState({ [modeKey]: 'photo' })}
              className="mr-2"
            >
              <i className="fa fa-camera mr-1" /> Upload Photo
            </Button>
            <Button
              color={mode === 'manual' ? 'primary' : 'outline-primary'}
              onClick={() => this.setState({ [modeKey]: 'manual' })}
            >
              <i className="fa fa-keyboard mr-1" /> Manual Entry
            </Button>
          </div>

          {mode === 'photo' ? (
            <div>
              {/* Show previously uploaded file */}
              {uploadedUrl && (
                <Alert color="info" className="mb-3">
                  <strong><i className="fa fa-check-circle mr-1" /> Document Uploaded</strong>
                  <br />
                  <small>
                    <a href={uploadedUrl} target="_blank" rel="noopener noreferrer">View uploaded image</a>
                  </small>
                  <br />
                  <small className="text-muted">You can upload a new photo to replace it</small>
                </Alert>
              )}

              <FormGroup>
                <Label>Upload {title} {uploadedUrl ? '(Replace)' : ''}</Label>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={this.handleFileChange(docType)}
                  disabled={this.state.uploadingDocument}
                />
                {this.state.uploadingDocument && <div className="text-info mt-2">Processing image...</div>}
              </FormGroup>

              {/* Show extracted data if available */}
              {Object.keys(data).some(key => data[key]) && (
                <Alert color="success">
                  <strong>Extracted Data:</strong> Review and edit if needed
                </Alert>
              )}
            </div>
          ) : null}

          {/* Manual entry fields or extracted data edit */}
          {this.renderDocumentFields(docType, data, dataState)}

          <div className="mt-3">
            <Button
              color="success"
              onClick={() => {
                this.saveManualData(docType, data);
              }}
              disabled={this.state.isSubmitting}
            >
              Save & Continue
            </Button>
            {stepNum > 4 && (
              <Button
                color="link"
                onClick={() => {
                  this.setState({ [`skip${docType.charAt(0).toUpperCase() + docType.slice(1)}`]: true });
                  this.completeStep();
                }}
              >
                Skip (Optional)
              </Button>
            )}
          </div>
        </CardBody>
      </Card>
    );
  }

  renderDocumentFields(docType, data, dataState) {
    if (docType === 'rc') {
      return (
        <div>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>Owner Name *</Label>
                <Input
                  value={data.owner_name || ''}
                  onChange={(e) => this.setState({ [dataState]: { ...data, owner_name: e.target.value } })}
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Registration Number *</Label>
                <Input
                  value={data.registration_number || ''}
                  onChange={(e) => this.setState({ [dataState]: { ...data, registration_number: e.target.value } })}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md={4}>
              <FormGroup>
                <Label for="rc-make">Make *</Label>
                <Input
                  type="text"
                  name="make"
                  id="rc-make"
                  autoComplete="on"
                  value={data.vehicle_make || ''}
                  onChange={(e) => this.setState({ [dataState]: { ...data, vehicle_make: e.target.value } })}
                  placeholder="e.g., MAHINDRA"
                />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label for="rc-model">Model *</Label>
                <Input
                  type="text"
                  name="model"
                  id="rc-model"
                  autoComplete="on"
                  value={data.vehicle_model || ''}
                  onChange={(e) => this.setState({ [dataState]: { ...data, vehicle_model: e.target.value } })}
                  placeholder="e.g., BOLERO CAMPER"
                />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label>Vehicle Class *</Label>
                <Input
                  type="select"
                  value={data.vehicle_class || ''}
                  onChange={(e) => this.setState({ [dataState]: { ...data, vehicle_class: e.target.value } })}
                >
                  <option value="">Select class</option>
                  {Array.from({ length: 18 }, (_, i) => i + 1).map((n) => (
                    <option key={n} value={String(n)}>Class {n}</option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md={4}>
              <FormGroup>
                <Label>Engine Number</Label>
                <Input
                  value={data.engine_number || ''}
                  onChange={(e) => this.setState({ [dataState]: { ...data, engine_number: e.target.value } })}
                />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label>Chassis Number</Label>
                <Input
                  value={data.chassis_number || ''}
                  onChange={(e) => this.setState({ [dataState]: { ...data, chassis_number: e.target.value } })}
                />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label>Fuel Type</Label>
                <Input
                  type="select"
                  value={data.fuel_type || ''}
                  onChange={(e) => this.setState({ [dataState]: { ...data, fuel_type: e.target.value } })}
                >
                  <option value="">Select...</option>
                  <option value="Diesel">Diesel</option>
                  <option value="Petrol">Petrol</option>
                  <option value="CNG">CNG</option>
                  <option value="Electric">Electric</option>
                </Input>
              </FormGroup>
            </Col>
          </Row>
        </div>
      );
    }

    if (docType === 'dl') {
      return (
        <div>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>Driver Name *</Label>
                <Input
                  value={data.name || ''}
                  onChange={(e) => this.setState({ [dataState]: { ...data, name: e.target.value } })}
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>DL Number *</Label>
                <Input
                  value={data.dl_number || ''}
                  onChange={(e) => this.setState({ [dataState]: { ...data, dl_number: e.target.value } })}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>Date of Birth</Label>
                <Input
                  type="date"
                  value={data.date_of_birth || ''}
                  onChange={(e) => this.setState({ [dataState]: { ...data, date_of_birth: e.target.value } })}
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Valid Till</Label>
                <Input
                  type="date"
                  value={data.valid_till || ''}
                  onChange={(e) => this.setState({ [dataState]: { ...data, valid_till: e.target.value } })}
                />
              </FormGroup>
            </Col>
          </Row>
          <FormGroup>
            <Label>Address</Label>
            <Input
              type="textarea"
              value={data.address || ''}
              onChange={(e) => this.setState({ [dataState]: { ...data, address: e.target.value } })}
            />
          </FormGroup>
        </div>
      );
    }

    if (docType === 'insurance') {
      return (
        <div>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>Policy Number *</Label>
                <Input
                  value={data.policy_number || ''}
                  onChange={(e) => this.setState({ [dataState]: { ...data, policy_number: e.target.value } })}
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Vehicle Number *</Label>
                <Input
                  value={data.vehicle_number || ''}
                  onChange={(e) => this.setState({ [dataState]: { ...data, vehicle_number: e.target.value } })}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>Policy Start Date</Label>
                <Input
                  type="date"
                  value={data.policy_start || ''}
                  onChange={(e) => this.setState({ [dataState]: { ...data, policy_start: e.target.value } })}
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Valid Till</Label>
                <Input
                  type="date"
                  value={data.valid_till || ''}
                  onChange={(e) => this.setState({ [dataState]: { ...data, valid_till: e.target.value } })}
                />
              </FormGroup>
            </Col>
          </Row>
        </div>
      );
    }

    if (docType === 'emission') {
      return (
        <div>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>Registration Number *</Label>
                <Input
                  value={data.registration_number || ''}
                  onChange={(e) => this.setState({ [dataState]: { ...data, registration_number: e.target.value } })}
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Certificate Number</Label>
                <Input
                  value={data.certificate_number || ''}
                  onChange={(e) => this.setState({ [dataState]: { ...data, certificate_number: e.target.value } })}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>Test Date</Label>
                <Input
                  type="date"
                  value={data.test_date || ''}
                  onChange={(e) => this.setState({ [dataState]: { ...data, test_date: e.target.value } })}
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Valid Till *</Label>
                <Input
                  type="date"
                  value={data.valid_till || ''}
                  onChange={(e) => this.setState({ [dataState]: { ...data, valid_till: e.target.value } })}
                />
              </FormGroup>
            </Col>
          </Row>
        </div>
      );
    }

    return null;
  }

  renderStep6_VehiclePhoto() {
    const { vehiclePhotoUrl, uploadingDocument } = this.state;

    return (
      <Card>
        <CardBody>
          <CardTitle tag="h5">Step 6: Vehicle Photo</CardTitle>

          {vehiclePhotoUrl && (
            <Alert color="info" className="mb-3">
              <strong><i className="fa fa-check-circle mr-1" /> Vehicle Photo Uploaded</strong>
              <br />
              <small>
                <a href={vehiclePhotoUrl} target="_blank" rel="noopener noreferrer">View uploaded photo</a>
              </small>
              <br />
              <small className="text-muted">You can upload a new photo to replace it</small>
            </Alert>
          )}

          <FormGroup>
            <Label>Upload Vehicle Photo {vehiclePhotoUrl ? '(Replace)' : ''}</Label>
            <Input
              type="file"
              accept="image/*"
              onChange={this.handleFileChange('vehicle')}
              disabled={uploadingDocument}
            />
            {uploadingDocument && <div className="text-info mt-2">Uploading...</div>}
          </FormGroup>
          <Button color="success" onClick={this.completeStep} disabled={!vehiclePhotoUrl}>
            Continue
          </Button>
        </CardBody>
      </Card>
    );
  }

  renderStep7_AdditionalInfo() {
    const {
      location, locationInput,
      vehicleType, specialization, shiftTiming, upiId,
      accountNumber, ifscCode, bankName, branchName
    } = this.state;

    return (
      <Card>
        <CardBody>
          <CardTitle tag="h5">Step 7: Additional Information</CardTitle>

          <FormGroup>
            <Label>Base Location * (start typing to search)</Label>
            <PlacesAutocompleteInput
              value={locationInput}
              onChange={(text) => this.setState({
                locationInput: text,
                // Clear confirmed location whenever user edits the text
                location: null,
                errorMessage: null,
              })}
              onPlaceSelect={this.handlePlaceSelect}
              placeholder="Type locality or area name..."
              inputStyle={{
                border: '1px solid #ced4da',
                borderRadius: '4px',
                padding: '6px 12px',
                fontSize: '14px',
                outline: 'none',
              }}
            />

            {location && (
              <Alert color="success" className="mt-2" style={{ marginBottom: 0 }}>
                <strong>✓ Selected:</strong> {location.address}
                <br />
                <small style={{ opacity: 0.8 }}>Lat: {location.latitude}, Lng: {location.longitude}</small>
              </Alert>
            )}
          </FormGroup>

          <div className="mt-3 mb-4">
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              zoom={location ? 15 : 11}
              center={location ? { lat: Number(location.latitude), lng: Number(location.longitude) } : defaultCenter}
              options={{
                styles: mapStyles,
                disableDefaultUI: false,
                zoomControl: true,
              }}
            >
              {location && (
                <Marker position={{ lat: Number(location.latitude), lng: Number(location.longitude) }} />
              )}
            </GoogleMap>
          </div>

          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>Vehicle Type</Label>
                <Input
                  type="select"
                  value={vehicleType}
                  onChange={(e) => {
                    const next = e.target.value;
                    this.setState({
                      vehicleType: next,
                      bodyType: next === 'closed' ? 'Closed' : 'Open',
                    });
                  }}
                >
                  <option value="open">Open</option>
                  <option value="closed">Closed</option>
                </Input>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Specialization</Label>
                <Input
                  type="select"
                  value={specialization}
                  onChange={(e) => this.setState({ specialization: e.target.value })}
                >
                  <option value="none">None</option>
                  <option value="refrigerated">Refrigerated</option>
                  <option value="tipper">Tipper</option>
                </Input>
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>Shift Timing</Label>
                <Input
                  type="select"
                  value={shiftTiming}
                  onChange={(e) => this.setState({ shiftTiming: e.target.value })}
                >
                  <option value="9am-7pm">9am-7pm (Day Shift)</option>
                  <option value="6am-5pm">6am-5pm (Early Shift)</option>
                  <option value="preferred">Preferred Time</option>
                </Input>
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>UPI ID</Label>
                <Input
                  value={upiId}
                  onChange={(e) => this.setState({ upiId: e.target.value })}
                  placeholder="driver@upi"
                />
              </FormGroup>
            </Col>
          </Row>

          <hr className="my-4" />
          <h6 className="mb-3">Bank Details (Optional)</h6>

          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>Account Number</Label>
                <Input
                  value={accountNumber}
                  onChange={(e) => this.setState({ accountNumber: e.target.value })}
                  placeholder="e.g., 1234567890"
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>IFSC Code</Label>
                <Input
                  value={ifscCode}
                  onChange={(e) => this.setState({ ifscCode: e.target.value.toUpperCase() })}
                  placeholder="e.g., SBIN0001234"
                  maxLength={11}
                />
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md={6}>
              <FormGroup>
                <Label>Bank Name</Label>
                <Input
                  value={bankName}
                  onChange={(e) => this.setState({ bankName: e.target.value })}
                  placeholder="e.g., State Bank of India"
                />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label>Branch Name</Label>
                <Input
                  value={branchName}
                  onChange={(e) => this.setState({ branchName: e.target.value })}
                  placeholder="e.g., MG Road Branch"
                />
              </FormGroup>
            </Col>
          </Row>


          <Button
            color="success"
            onClick={this.saveAdditionalInfo}
            disabled={this.state.isSubmitting || !location}
          >
            {this.state.isSubmitting ? 'Saving...' : 'Save & Continue'}
          </Button>
        </CardBody>
      </Card>
    );
  }

  renderStepRoutes() {
    const { routes, agencyRouteOptions, errors, isSubmitting, rcData } = this.state;
    const vehicleClass = rcData?.vehicle_class ? Number(rcData.vehicle_class) : null;
    const matchingAgencyRoutes = agencyRouteOptions.filter((row) => {
      if (row.vehicleClass == null) return true; // legacy
      if (vehicleClass == null) return true;
      return Number(row.vehicleClass) === vehicleClass;
    });
    const inputStyle = {
      border: '1px solid #ced4da',
      borderRadius: '4px',
      padding: '6px 12px',
      fontSize: '14px',
      outline: 'none',
      width: '100%',
    };

    return (
      <Card>
        <CardBody>
          <CardTitle tag="h5">Step 8: Vehicle Routes</CardTitle>
          <Alert color="info">
            Select agency routes for this vehicle
            {vehicleClass ? ` (Class ${vehicleClass})` : ''}
            . You can also add new routes for this vehicle only.
            Saved routes go on the vehicle table.
          </Alert>

          <h6 className="mb-2">
            Agency routes{vehicleClass ? ` · Class ${vehicleClass}` : ''}
          </h6>
          {!matchingAgencyRoutes.length ? (
            <Alert color="warning">
              {vehicleClass
                ? `No Class ${vehicleClass} routes on this transporter profile. Add new routes below, or pick a matching vehicle class.`
                : 'No routes on this transporter profile. Add new routes below for this vehicle.'}
            </Alert>
          ) : (
            matchingAgencyRoutes.map((row) => (
              <div
                key={row.id}
                className="mb-2 p-3"
                style={{
                  border: row.selected ? '1px solid #2563eb' : '1px solid #e5e7eb',
                  borderRadius: 8,
                  background: row.selected ? '#eff6ff' : '#fff',
                }}
              >
                <FormGroup check className="mb-0">
                  <Label check style={{ cursor: 'pointer', width: '100%' }}>
                    <Input
                      type="checkbox"
                      checked={!!row.selected}
                      onChange={() => this.toggleAgencyRoute(row.id)}
                    />{' '}
                    {row.vehicleClass != null && (
                      <span className="badge badge-secondary mr-2">Class {row.vehicleClass}</span>
                    )}
                    <strong>
                      {(row.from && row.from.address) || 'others'} → {(row.to && row.to.address) || 'others'}
                    </strong>
                    <span className="ml-2 text-muted">
                      {row.priceMax && Number(row.priceMax) !== Number(row.priceMin)
                        ? `₹${row.priceMin}–₹${row.priceMax}`
                        : `₹${row.priceMin}`}
                    </span>
                  </Label>
                </FormGroup>
              </div>
            ))
          )}

          <div className="d-flex justify-content-between align-items-center mt-4 mb-2">
            <h6 className="mb-0">Additional routes for this vehicle</h6>
            <Button color="secondary" outline size="sm" onClick={this.addRouteRow}>
              + Add route
            </Button>
          </div>

          {!routes.length && (
            <p className="text-muted" style={{ fontSize: 13 }}>
              Optional. Use this to add a route that is not on the agency profile.
            </p>
          )}

          {routes.map((row, index) => (
            <div
              key={row.id || `custom-${index}`}
              className="mb-3 p-3"
              style={{ border: '1px solid #e5e7eb', borderRadius: 8 }}
            >
              <div className="d-flex justify-content-between align-items-center mb-2">
                <strong>New route {index + 1}</strong>
                <Button color="link" className="text-danger p-0" onClick={() => this.removeRouteRow(index)}>
                  Remove
                </Button>
              </div>
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label>From *</Label>
                    <PlacesAutocompleteInput
                      value={row.fromInput || ''}
                      onChange={(text) => {
                        const next = [...this.state.routes];
                        next[index] = { ...next[index], fromInput: text, from: null };
                        this.setState({ routes: next, errors: {} });
                      }}
                      onPlaceSelect={(place) => this.handleRoutePlaceSelect(index, 'from', place)}
                      placeholder="Type city / area..."
                      inputStyle={inputStyle}
                    />
                    {row.from && (
                      <small className="text-success">Selected: {row.from.address}</small>
                    )}
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label>To (optional)</Label>
                    <PlacesAutocompleteInput
                      value={row.toInput || ''}
                      onChange={(text) => {
                        const next = [...this.state.routes];
                        next[index] = { ...next[index], toInput: text, to: null };
                        this.setState({ routes: next, errors: {} });
                      }}
                      onPlaceSelect={(place) => this.handleRoutePlaceSelect(index, 'to', place)}
                      placeholder="Type city / area..."
                      inputStyle={inputStyle}
                    />
                    {row.to && (
                      <small className="text-success">Selected: {row.to.address}</small>
                    )}
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <FormGroup>
                    <Label>Price / Min (optional)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={row.priceMin}
                      onChange={(e) => {
                        const next = [...this.state.routes];
                        next[index] = { ...next[index], priceMin: e.target.value };
                        this.setState({ routes: next, errors: {} });
                      }}
                      placeholder="3000"
                    />
                  </FormGroup>
                </Col>
                <Col md={6}>
                  <FormGroup>
                    <Label>Max Price (optional)</Label>
                    <Input
                      type="number"
                      min="0"
                      value={row.priceMax}
                      onChange={(e) => {
                        const next = [...this.state.routes];
                        next[index] = { ...next[index], priceMax: e.target.value };
                        this.setState({ routes: next, errors: {} });
                      }}
                      placeholder="Leave blank for single price"
                    />
                  </FormGroup>
                </Col>
              </Row>
            </div>
          ))}

          {errors.routes && <div className="text-danger mb-2">{errors.routes}</div>}
          {Object.keys(errors)
            .filter((k) => k.startsWith('route_'))
            .map((k) => (
              <div key={k} className="text-danger mb-1">{errors[k]}</div>
            ))}

          <div>
            <Button color="primary" onClick={this.saveRoutes} disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save Vehicle Routes & Continue'}
            </Button>
          </div>
        </CardBody>
      </Card>
    );
  }

  renderStep8_Review() {
    const {
      phoneNumber, rcData, location, driverName, isDriverOwner, ownerName, ownerPhone,
      isTransporter, transporterProfile, agencyRouteOptions, routes,
    } = this.state;
    const reviewStepNum = isTransporter ? 9 : 8;

    return (
      <Card>
        <CardBody>
          <CardTitle tag="h5">Step {reviewStepNum}: Review & Submit</CardTitle>

          <Alert color="info">
            <h6>Summary</h6>
            <p><strong>Phone:</strong> {phoneNumber}</p>
            <p><strong>Driver:</strong> {driverName}</p>
            {isTransporter && transporterProfile && (
              <>
                <p><strong>Type:</strong> Transporter Vehicle</p>
                <p><strong>Agency:</strong> {transporterProfile.agency_name}</p>
                <p><strong>Agency contact:</strong> {transporterProfile.contact_name}</p>
                <p>
                  <strong>Ownership:</strong>{' '}
                  {this.state.transporterIsDriver
                    ? 'Transporter is the driver'
                    : 'Separate driver — transporter is vehicle owner'}
                </p>
              </>
            )}
            {!isDriverOwner && !isTransporter && (
              <>
                <p><strong>Owner:</strong> {ownerName}</p>
                <p><strong>Owner Phone:</strong> {ownerPhone}</p>
              </>
            )}
            <p><strong>Vehicle:</strong> {rcData.vehicle_make} {rcData.vehicle_model} ({rcData.registration_number})</p>
            <p><strong>Vehicle Class:</strong> {rcData.vehicle_class ? `Class ${rcData.vehicle_class}` : 'Not set'}</p>
            <p><strong>Base:</strong> {location?.address || 'Not set'}</p>
            {isTransporter && (
              <div>
                <strong>Routes on this vehicle:</strong>
                <ul className="mb-0 mt-1">
                  {[
                    ...(agencyRouteOptions || []).filter((r) => r.selected),
                    ...(routes || []),
                  ].map((r, i) => (
                    <li key={`rev-route-${i}`}>
                      {(r.from && r.from.address) || 'others'} → {(r.to && r.to.address) || 'others'}
                      {' '}
                      (
                      {r.priceMax && Number(r.priceMax) !== Number(r.priceMin)
                        ? `₹${r.priceMin}–₹${r.priceMax}`
                        : `₹${r.priceMin}`}
                      )
                      {r.source === 'custom' ? ' · new' : ''}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Alert>

          <h6 className="mb-3">Vehicle details</h6>
          <Row>
            <Col md={4}>
              <FormGroup>
                <Label>Registration Number</Label>
                <Input
                  value={rcData.registration_number || ''}
                  onChange={(e) => this.setState({
                    rcData: { ...this.state.rcData, registration_number: e.target.value },
                  })}
                  placeholder="REG-155"
                />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label for="make">Make</Label>
                <Input
                  type="text"
                  name="make"
                  id="make"
                  autoComplete="on"
                  value={rcData.vehicle_make || ''}
                  onChange={(e) => this.setState({
                    rcData: { ...this.state.rcData, vehicle_make: e.target.value },
                  })}
                />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label for="model">Model</Label>
                <Input
                  type="text"
                  name="model"
                  id="model"
                  autoComplete="on"
                  value={rcData.vehicle_model || ''}
                  onChange={(e) => this.setState({
                    rcData: { ...this.state.rcData, vehicle_model: e.target.value },
                  })}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md={4}>
              <FormGroup>
                <Label>Length</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={this.state.lengthFt}
                  onChange={(e) => this.setState({ lengthFt: e.target.value })}
                />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label>Breadth</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={this.state.breadthFt}
                  onChange={(e) => this.setState({ breadthFt: e.target.value })}
                />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label>Height</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={this.state.heightFt}
                  onChange={(e) => this.setState({ heightFt: e.target.value })}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md={4}>
              <FormGroup>
                <Label>Body Type</Label>
                <Input
                  type="select"
                  value={this.state.bodyType || 'Open'}
                  onChange={(e) => this.setState({
                    bodyType: e.target.value,
                    vehicleType: e.target.value === 'Closed' ? 'closed' : 'open',
                  })}
                >
                  <option value="Open">Open</option>
                  <option value="Closed">Closed</option>
                  <option value="Container">Container</option>
                </Input>
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label>Vehicle Class *</Label>
                <Input
                  type="select"
                  value={rcData.vehicle_class || ''}
                  onChange={(e) => this.setState({
                    rcData: { ...this.state.rcData, vehicle_class: e.target.value },
                    errorMessage: null,
                  })}
                >
                  <option value="">Select vehicle class</option>
                  {Array.from({ length: 18 }, (_, i) => i + 1).map((n) => (
                    <option key={n} value={String(n)}>Class {n}</option>
                  ))}
                </Input>
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label>Specialization</Label>
                <Input
                  type="select"
                  value={(() => {
                    const spec = String(this.state.specialization || 'none').toLowerCase();
                    if (spec === 'refrigerated') return 'Refrigerated';
                    if (spec === 'tipper') return 'Tipper';
                    return 'none';
                  })()}
                  onChange={(e) => this.setState({ specialization: e.target.value })}
                >
                  <option value="none">None</option>
                  <option value="Tipper">Tipper</option>
                  <option value="Refrigerated">Refrigerated</option>
                </Input>
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md={4}>
              <FormGroup>
                <Label>Tonnage (ton)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={this.state.tonnage}
                  onChange={(e) => this.setState({ tonnage: e.target.value })}
                  placeholder="e.g. 1.5"
                />
              </FormGroup>
            </Col>
            <Col md={4}>
              <FormGroup>
                <Label>Per KM Price (₹)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={this.state.perKmPrice}
                  onChange={(e) => this.setState({ perKmPrice: e.target.value })}
                  placeholder="e.g. 15.50"
                />
              </FormGroup>
            </Col>
          </Row>
          <FormGroup>
            <Label>Remarks</Label>
            <Input
              type="textarea"
              rows={3}
              value={this.state.remarks}
              onChange={(e) => this.setState({ remarks: e.target.value })}
              placeholder="No remarks added"
            />
          </FormGroup>
          <FormGroup>
            <Label>Phone Extensions / Alternate Contacts</Label>
            {this.state.phoneExtensions.length > 0 ? (
              <div className="mb-2" style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {this.state.phoneExtensions.map((phone) => (
                  <Badge key={phone} color="light" className="border" style={{ fontSize: 13 }}>
                    {phone}{' '}
                    <button
                      type="button"
                      onClick={() => this.setState((prev) => ({
                        phoneExtensions: prev.phoneExtensions.filter((p) => p !== phone),
                      }))}
                      style={{ border: 'none', background: 'none', color: '#dc2626', cursor: 'pointer' }}
                    >
                      &times;
                    </button>
                  </Badge>
                ))}
              </div>
            ) : (
              <div className="text-muted mb-2" style={{ fontSize: 13 }}>No alternate phone numbers added.</div>
            )}
            <div className="d-flex" style={{ gap: 8, maxWidth: 420 }}>
              <Input
                type="tel"
                value={this.state.newPhoneExtInput}
                onChange={(e) => this.setState({ newPhoneExtInput: e.target.value })}
                placeholder="e.g. 919900000000"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    this.addPhoneExtension();
                  }
                }}
              />
              <Button type="button" color="primary" onClick={this.addPhoneExtension}>Add</Button>
            </div>
            {this.state.errors.phoneExt && <div className="text-danger mt-1">{this.state.errors.phoneExt}</div>}
          </FormGroup>
          <FormGroup>
            <Label>Trip Preferences</Label>
            <Row>
              {[
                ['intraStatePermit', 'All India permit'],
                ['readyForLongTrip', 'Ready for Long Trip'],
                ['longInterDistrict', 'Long Intra-District'],
                ['longInterState', 'Long Intra-State'],
                ['local', 'Local'],
              ].map(([key, label]) => (
                <Col md={6} key={key}>
                  <FormGroup check className="mb-2">
                    <Label check>
                      <Input
                        type="checkbox"
                        checked={!!this.state[key]}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          if (key === 'intraStatePermit' && checked) {
                            this.setState({
                              intraStatePermit: true,
                              readyForLongTrip: true,
                              longInterState: true,
                            });
                            return;
                          }
                          this.setState({ [key]: checked });
                        }}
                      />{' '}
                      {label}
                    </Label>
                  </FormGroup>
                </Col>
              ))}
            </Row>
          </FormGroup>

          <div className="mb-3">
            <Button color="link" onClick={() => this.goToStep(2)}>Edit RC</Button>
            {' | '}
            <Button color="link" onClick={() => this.goToStep(3)}>Edit DL</Button>
            {' | '}
            <Button color="link" onClick={() => this.goToStep(7)}>Edit Location</Button>
            {isTransporter && (
              <>
                {' | '}
                <Button color="link" onClick={() => this.goToStep(8)}>Edit Routes</Button>
              </>
            )}
          </div>

          <div>
            <Alert color="info" className="mb-3">
              <strong>Option A — Complete directly:</strong> Finishes onboarding immediately. Vehicle, driver,
              and all records are created now.
              <br />
              <strong>Option B — Send Terms via WhatsApp:</strong> Sends the T&amp;C message to the driver.
              Onboarding completes automatically once the driver taps &quot;I Agree&quot;.
            </Alert>

            <div className="d-flex gap-3 flex-wrap">
              <Button
                color="primary"
                size="lg"
                onClick={this.completeOnboarding}
                disabled={this.state.isSubmitting || this.state.isSendingTerms || this.state.termsSent}
              >
                {this.state.isSubmitting ? 'Completing...' : 'Complete Onboarding Directly'}
              </Button>

              <Button
                color="secondary"
                size="lg"
                onClick={this.sendTermsToDriver}
                disabled={this.state.isSendingTerms || this.state.isSubmitting || this.state.termsSent}
              >
                {this.state.isSendingTerms
                  ? 'Sending...'
                  : this.state.termsSent
                  ? 'Terms Sent ✓'
                  : 'Send Terms to Driver'}
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>
    );
  }

  render() {
    const { currentStep, successMessage, errorMessage, existingVehicles, isSubmitting } = this.state;

    if (currentStep === 999) {
      return (
        <div>
          <Alert color="success" className="text-center">
            <h4>Onboarding Complete</h4>
            <p>{successMessage || 'Driver successfully onboarded. Redirecting...'}</p>
          </Alert>
        </div>
      );
    }

    return (
      <div>
        <Breadcrumb>
          <BreadcrumbItem>YOU ARE HERE</BreadcrumbItem>
          <BreadcrumbItem>Vehicles</BreadcrumbItem>
          <BreadcrumbItem active>
            {this.state.isTransporter ? 'Transporter Onboarding (New)' : 'Driver Onboarding (New)'}
          </BreadcrumbItem>
        </Breadcrumb>

        <Row>
          <Col lg={10} className="mx-auto">
            <Widget
              title={
                <h5 className="mt-0 mb-0">
                  <i className="fa fa-user-plus mr-xs" />
                  {this.state.isTransporter
                    ? 'Transporter Onboarding via OPS'
                    : 'Driver Onboarding via OPS'}
                </h5>
              }
            >
              {successMessage && <Alert color="success">{successMessage}</Alert>}
              {errorMessage && (
                <Alert color="danger">
                  <strong>{errorMessage}</strong>
                  {existingVehicles && existingVehicles.length > 0 && (
                    <ul className="mb-0 mt-2">
                      {existingVehicles.map((v) => (
                        <li key={v.vehicle_id}>
                          Vehicle ID {v.vehicle_id}
                          {v.registration_no ? ` · ${v.registration_no}` : ''}
                          {v.driver_name ? ` · Driver: ${v.driver_name}` : ''}
                        </li>
                      ))}
                    </ul>
                  )}
                </Alert>
              )}

              {this.renderStepIndicator()}

              {currentStep === 1 && this.renderStep1_PhoneNumber()}
              {currentStep === 2 && this.renderDocumentStep(2, 'RC Details', 'rc', 'rcData', 'rcData', 'rcMode')}
              {currentStep === 3 && this.renderDocumentStep(3, 'DL Details', 'dl', 'dlData', 'dlData', 'dlMode')}
              {currentStep === 4 && this.renderDocumentStep(4, 'Insurance Details', 'insurance', 'insuranceData', 'insuranceData', 'insuranceMode')}
              {currentStep === 5 && this.renderDocumentStep(5, 'Emission Certificate', 'emission', 'emissionData', 'emissionData', 'emissionMode')}
              {currentStep === 6 && this.renderStep6_VehiclePhoto()}
              {currentStep === 7 && this.renderStep7_AdditionalInfo()}
              {currentStep === 8 && this.state.isTransporter && this.renderStepRoutes()}
              {currentStep === 8 && !this.state.isTransporter && this.renderStep8_Review()}
              {currentStep === 9 && this.state.isTransporter && this.renderStep8_Review()}
            </Widget>
          </Col>
        </Row>
      </div>
    );
  }
}

export default connect()(DriverOnboardingNew);
